/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Mail, MapPin, Navigation, Phone, Search, X } from 'lucide-react';
import { useHeaderStates } from '../../Providers/HeaderStates';
import { useStartupState } from '../../Providers/StartupState';
import { Loading } from '../../components/Loading';
import { Foto } from '../Sustainability/comum';
import { FaixaFinal, TituloComDestaque } from '../News';
import {
  ROTULOS,
  aplicarMetadados,
  carregarUnidades,
  contagem,
  filtrar,
  mapaDe,
  opcoes,
  rotaPara,
  telefoneLink,
} from './dados';
import * as S from './style';

const Contato = ({ icone: Icone, rotulo, children }) => (
  <S.Contato>
    <Icone size={16} aria-hidden="true" />
    <span>
      {rotulo && <small>{rotulo}</small>}
      {children}
    </span>
  </S.Contato>
);

// Abre o mapa pela foto ou pelo "Ver localização". A foto fica fora da ordem
// do Tab (o mesmo botão logo abaixo já está nela).
const Cartao = ({ unidade, aoAbrir }) => (
  <S.Cartao>
    <S.CartaoFoto type="button" onClick={() => aoAbrir(unidade)} tabIndex={-1} aria-hidden="true">
      <Foto id={unidade.foto} largura={800} alt="" />
      <span>{unidade.tipo}</span>
    </S.CartaoFoto>
    <S.CartaoCorpo>
      <p>
        {unidade.cidade} · {unidade.estado}
      </p>
      <h2>{unidade.nome}</h2>
      <Contato icone={MapPin}>{unidade.endereco}</Contato>
      {unidade.telefone && <Contato icone={Phone}>{unidade.telefone}</Contato>}
      {unidade.email && <Contato icone={Mail}>{unidade.email}</Contato>}
      <S.CartaoRodape type="button" onClick={() => aoAbrir(unidade)} aria-label={`${ROTULOS.verLocalizacao}: ${unidade.nome}`}>
        {ROTULOS.verLocalizacao} <ArrowRight size={17} aria-hidden="true" />
      </S.CartaoRodape>
    </S.CartaoCorpo>
  </S.Cartao>
);

// Quantas unidades a grade mostra de cada vez (cabe certo em 3, 2 ou 1 coluna).
const POR_VEZ = 12;

// Painel lateral com o mapa. É um <dialog> nativo: o navegador prende o foco
// dentro dele e fecha com Esc; aqui só se fecha também clicando fora.
const Painel = ({ unidade, aoFechar }) => {
  const ref = useRef(null);
  useEffect(() => {
    const dialogo = ref.current;
    dialogo.showModal();
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  return (
    <S.Painel
      ref={ref}
      aria-labelledby="unidade-titulo"
      onClose={aoFechar}
      onClick={(e) => {
        if (e.target === ref.current) ref.current.close();
      }}
    >
      <S.PainelConteudo>
        <S.PainelTopo>
          <div>
            <S.Chapeu>{unidade.tipo}</S.Chapeu>
            <h2 id="unidade-titulo">{unidade.nome}</h2>
            <span>
              {unidade.cidade} · {unidade.estado}
            </span>
          </div>
          <button type="button" onClick={() => ref.current.close()} aria-label={ROTULOS.fechar}>
            <X size={20} />
          </button>
        </S.PainelTopo>
        <S.Mapa>
          <iframe title={`${ROTULOS.mapa}: ${unidade.nome}`} src={mapaDe(unidade)} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </S.Mapa>
        <S.LegendaMapa>{ROTULOS.legendaMapa}</S.LegendaMapa>
        <S.Detalhes>
          <h3>{ROTULOS.informacoes}</h3>
          <Contato icone={MapPin} rotulo={ROTULOS.endereco}>
            {unidade.endereco}
            <br />
            {unidade.cidade} · {unidade.estado}
          </Contato>
          {unidade.telefone && (
            <Contato icone={Phone} rotulo={ROTULOS.telefone}>
              <a href={telefoneLink(unidade.telefone)}>{unidade.telefone}</a>
            </Contato>
          )}
          {unidade.email && (
            <Contato icone={Mail} rotulo={ROTULOS.email}>
              <a href={`mailto:${unidade.email}`}>{unidade.email}</a>
            </Contato>
          )}
          <S.Rota href={rotaPara(unidade)} target="_blank" rel="noopener noreferrer">
            <Navigation size={18} aria-hidden="true" /> {ROTULOS.tracarRota}
          </S.Rota>
        </S.Detalhes>
      </S.PainelConteudo>
    </S.Painel>
  );
};

export const OurUnits = () => {
  const [dados, setDados] = useState(null);
  const [pronto, setPronto] = useState(false);
  const [busca, setBusca] = useState('');
  const [cidade, setCidade] = useState('');
  const [tipo, setTipo] = useState('');
  const [aberta, setAberta] = useState(null);
  const [quantas, setQuantas] = useState(POR_VEZ);

  const { setTitle, setBgHeader } = useHeaderStates();
  const { token, setError, setLoading } = useStartupState();

  useEffect(() => {
    setError(null);
    setLoading(true);
    if (!token) return;
    carregarUnidades(token)
      .then(setDados)
      .catch((e) => console.log(e.message))
      .finally(() => {
        setPronto(true);
        setLoading(false);
      });
  }, [token]);

  // A página tem o próprio topo: o banner do Header fica desligado.
  useEffect(() => {
    setTitle(null);
    setBgHeader(null);
  });

  useEffect(() => {
    if (!dados) return undefined;
    return aplicarMetadados(dados.metadados);
  }, [dados]);

  const cidades = useMemo(() => opcoes(dados?.lista || [], 'cidade'), [dados]);
  const tipos = useMemo(() => opcoes(dados?.lista || [], 'tipo'), [dados]);
  const encontradas = useMemo(() => filtrar(dados?.lista || [], { busca, cidade, tipo }), [dados, busca, cidade, tipo]);

  // Filtro novo começa do primeiro bloco de novo.
  useEffect(() => setQuantas(POR_VEZ), [busca, cidade, tipo]);

  if (!pronto) return <Loading />;
  if (!dados) return null;

  const { topo } = dados;
  const visiveis = encontradas.slice(0, quantas);
  const filtrando = Boolean(busca.trim() || cidade || tipo);
  const limpar = () => {
    setBusca('');
    setCidade('');
    setTipo('');
  };

  return (
    <S.Pagina>
      <S.Container>
        <S.TrilhaDaPagina />

        <S.Abertura>
          <div>
            <TituloComDestaque titulo={topo.titulo} destaque={topo.destaque} />
            {topo.descricao && <p>{topo.descricao}</p>}
          </div>
          {topo.chamada && (
            <S.Presenca>
              <MapPin size={34} strokeWidth={1.5} aria-hidden="true" />
              <strong>{topo.chamada}</strong>
            </S.Presenca>
          )}
        </S.Abertura>

        <S.Filtros aria-label={ROTULOS.busca}>
          <div>
            <label htmlFor="busca-unidade">{ROTULOS.busca}</label>
            <S.CampoBusca>
              <Search size={18} aria-hidden="true" />
              <input
                id="busca-unidade"
                type="search"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder={ROTULOS.buscaPlaceholder}
              />
            </S.CampoBusca>
          </div>
          <div>
            <label htmlFor="filtro-cidade">{ROTULOS.cidade}</label>
            <select id="filtro-cidade" value={cidade} onChange={(e) => setCidade(e.target.value)}>
              <option value="">{ROTULOS.todasCidades}</option>
              {cidades.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="filtro-tipo">{ROTULOS.tipo}</label>
            <select id="filtro-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}>
              <option value="">{ROTULOS.todosTipos}</option>
              {tipos.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </div>
        </S.Filtros>

        <S.Resultado>
          <span aria-live="polite">
            <strong>{contagem(encontradas.length, ROTULOS.unidade)}</strong> · {cidade || ROTULOS.todasCidades}
          </span>
          {filtrando ? (
            <button type="button" onClick={limpar}>
              {ROTULOS.limpar} <X size={13} aria-hidden="true" />
            </button>
          ) : (
            <small>{ROTULOS.dica} ↗</small>
          )}
        </S.Resultado>

        <S.Grade>
          {visiveis.length ? (
            visiveis.map((u) => <Cartao key={u.id} unidade={u} aoAbrir={setAberta} />)
          ) : (
            <S.Vazio>
              <h2>{ROTULOS.vazioTitulo}</h2>
              <p>{ROTULOS.vazioTexto}</p>
            </S.Vazio>
          )}
        </S.Grade>

        {encontradas.length > quantas && (
          <S.CarregarMais type="button" onClick={() => setQuantas(quantas + POR_VEZ)}>
            {ROTULOS.carregarMais}
          </S.CarregarMais>
        )}
        {visiveis.length > 0 && encontradas.length <= quantas && <S.Fim>{ROTULOS.fim}</S.Fim>}
      </S.Container>

      <FaixaFinal faixa={dados.faixaFinal} />

      {aberta && <Painel unidade={aberta} aoFechar={() => setAberta(null)} />}
    </S.Pagina>
  );
};
